import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  chakra,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Download, FileAudio, FileText, FileVideo, Play, X } from 'lucide-react';
import { useState } from 'react';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { testIds } from './testIds';

type PlayableKind = 'audio' | 'video';

type PlayerState =
  | { status: 'closed' }
  | { status: 'loading' }
  | { status: 'open'; url: string }
  | { status: 'error'; reason: 'urlFailed' | 'playbackFailed' };

export function playableKind(mimetype: string | null | undefined): PlayableKind | null {
  if (mimetype?.startsWith('audio/') === true) {
    return 'audio';
  }
  if (mimetype?.startsWith('video/') === true) {
    return 'video';
  }
  return null;
}

function formatFileSize(sizeInBytes: number): string {
  if (sizeInBytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(sizeInBytes / 1024))} KB`;
  }
  return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A file a student has submitted. Audio and video can be played inline when
 * `getPlaybackUrl` is given; the URL is only requested when the viewer
 * presses Play, so listing many files signs nothing up front.
 */
export function SubmittedFile({
  filename,
  mimetype,
  sizeInBytes,
  onDownload,
  getPlaybackUrl,
  size = 'md',
}: {
  filename: string;
  mimetype?: string | null;
  sizeInBytes?: number | null;
  onDownload: () => Promise<void>;
  getPlaybackUrl?: () => Promise<string>;
  size?: 'sm' | 'md';
}) {
  const [player, setPlayer] = useState<PlayerState>({ status: 'closed' });
  const kind = getPlaybackUrl ? playableKind(mimetype) : null;
  const FileIcon = kind === 'audio' ? FileAudio : kind === 'video' ? FileVideo : FileText;

  const openPlayer = async () => {
    if (!getPlaybackUrl) {
      return;
    }
    setPlayer({ status: 'loading' });
    try {
      setPlayer({ status: 'open', url: await getPlaybackUrl() });
    } catch {
      setPlayer({ status: 'error', reason: 'urlFailed' });
    }
  };

  const details = [
    kind === 'audio' ? 'Audio' : kind === 'video' ? 'Video' : null,
    typeof sizeInBytes === 'number' ? formatFileSize(sizeInBytes) : null,
  ].filter((detail) => detail !== null);

  return (
    <VStack
      data-testid={testIds.submittedFile}
      w="full"
      alignItems="stretch"
      spacing={3}
      borderRadius="md"
      borderColor="border.emphasis"
      borderWidth="1px"
      bgColor="bg.subtle"
      p={size === 'sm' ? 2 : 4}
    >
      <HStack spacing={4} justifyContent="space-between">
        <HStack spacing={2} minW={0}>
          <Box flexShrink={0} color="fg.emphasis">
            <FileIcon size={iconSize['2xl']} aria-hidden />
          </Box>
          <VStack spacing={0} alignItems="flex-start" minW={0}>
            <Button
              variant="link"
              color="fg.emphasis"
              maxW="full"
              onClick={() => void onDownload()}
            >
              <Text isTruncated>{filename}</Text>
            </Button>
            {details.length > 0 ? (
              <Text fontSize="xs" color="fg.muted">
                {details.join(' · ')}
              </Text>
            ) : null}
          </VStack>
        </HStack>

        <HStack spacing={1} flexShrink={0}>
          {kind !== null && (player.status === 'closed' || player.status === 'loading') ? (
            <Button
              data-testid={testIds.playButton}
              size="sm"
              variant="secondary"
              leftIcon={<Play size={iconSize.md} aria-hidden />}
              isLoading={player.status === 'loading'}
              onClick={() => void openPlayer()}
            >
              Play
            </Button>
          ) : null}
          {kind !== null && (player.status === 'open' || player.status === 'error') ? (
            <IconButton
              data-testid={testIds.closePlayerButton}
              aria-label="Close player"
              icon={<X size={iconSize.lg} />}
              size="sm"
              variant="ghost"
              color="fg.default"
              onClick={() => setPlayer({ status: 'closed' })}
            />
          ) : null}
          <IconButton
            aria-label={`Download ${filename}`}
            icon={<Download size={iconSize.lg} />}
            variant="ghost"
            color="fg.default"
            onClick={() => void onDownload()}
          />
        </HStack>
      </HStack>

      {kind !== null && player.status === 'open' ? (
        kind === 'video' ? (
          <chakra.video
            data-testid={testIds.player}
            src={player.url}
            controls
            autoPlay
            playsInline
            preload="metadata"
            w="full"
            maxH={pxToRem(480)}
            bg="black"
            borderRadius="md"
            onError={() => setPlayer({ status: 'error', reason: 'playbackFailed' })}
          />
        ) : (
          <chakra.audio
            data-testid={testIds.player}
            src={player.url}
            controls
            autoPlay
            preload="metadata"
            w="full"
            onError={() => setPlayer({ status: 'error', reason: 'playbackFailed' })}
          />
        )
      ) : null}

      {player.status === 'error' ? (
        <Alert status="warning" borderRadius="md" role="status">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            {player.reason === 'urlFailed'
              ? 'This file could not be loaded. Try again, or download it instead.'
              : 'This file cannot be played in your browser. Download it to play it.'}
            {player.reason === 'urlFailed' ? (
              <Button ml={2} size="sm" variant="link" onClick={() => void openPlayer()}>
                Try again
              </Button>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
    </VStack>
  );
}
